import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { billsApi, servicesApi, customerCouponsApi } from '../../services/smartBillApi';
import axios from 'axios';
import ScratchCard from '../../components/ScratchCard';
import CouponReelsModal from '../../components/CouponReelsModal';
import { Tabs, Tab, Box, useMediaQuery, useTheme } from '@mui/material';
import './CustomerDashboard.css';

// Use env var if set, otherwise use relative URL (works for same-domain backend)
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    let base = process.env.REACT_APP_API_URL;
    // Remove trailing slash
    base = base.endsWith('/') ? base.slice(0, -1) : base;
    // Remove /api suffix if present (we'll add it back)
    if (base.endsWith('/api')) {
      base = base.slice(0, -4);
    }
    return base;
  }
  // In production (ondigitalocean.app or services.knls.in), use relative URL
  if (typeof window !== 'undefined' && (window.location.hostname.includes('ondigitalocean.app') || window.location.hostname.includes('services.knls.in'))) {
    return ''; // Empty string means relative URL - will become /api/... when concatenated
  }
  // Development fallback
  return 'http://localhost:3000';
};

const formatCurrency = (value) => {
  const number = Number(value) || 0;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
};

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams] = useSearchParams();
  const phone = searchParams.get('phone') || '';
  const customerName = searchParams.get('name') || '';
  const billNumber = searchParams.get('bill') || '';
  const tabParam = searchParams.get('tab') || '';
  const isHistoryMode = searchParams.get('history') === 'true';

  // Set initial tab from URL parameter
  // In history mode, force coupons tab and don't allow switching
  // Default to 'coupons' tab instead of 'services'
  const [activeTab, setActiveTab] = useState(isHistoryMode ? 'coupons' : (tabParam || 'coupons'));
  // Coupon status filter: active, used, expired (default: active)
  const [couponStatusFilter, setCouponStatusFilter] = useState('active');
  const [loading, setLoading] = useState(false);
  const [utilizedServices, setUtilizedServices] = useState([]);
  const [pendingWorks, setPendingWorks] = useState([]);
  const [appliedJobs, setAppliedJobs] = useState([]);
  const [customerCoupons, setCustomerCoupons] = useState([]);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [updatingCoupons, setUpdatingCoupons] = useState(false);
  const [openScratchCard, setOpenScratchCard] = useState(null);
  const [statistics, setStatistics] = useState({
    totalServices: 0,
    totalSpent: 0,
    pendingWorksCount: 0,
    appliedJobsCount: 0,
    activeCoupons: 0,
  });
  
  // Touch event tracking for mobile tap vs scroll
  const touchStartRef = useRef({ x: 0, y: 0, time: 0, index: -1 });
  const isScrollingRef = useRef(false);

  useEffect(() => {
    if (phone) {
      fetchAllData();
    }
  }, [phone]);

  // Refetch coupons only when switching *to* the coupons tab (avoids duplicate calls on mount)
  const prevTabRef = useRef(activeTab);
  useEffect(() => {
    if (activeTab === 'coupons' && phone && prevTabRef.current !== 'coupons') {
      fetchCustomerCoupons();
    }
    prevTabRef.current = activeTab;
  }, [activeTab, phone]);

  // Update active tab when tab parameter changes
  // In history mode, always force coupons tab
  useEffect(() => {
    if (isHistoryMode) {
      setActiveTab('coupons');
    } else if (tabParam && ['services', 'pending', 'jobs', 'coupons'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [tabParam, isHistoryMode]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchUtilizedServices(),
        fetchPendingWorks(),
        fetchAppliedJobs(),
        fetchCustomerCoupons(),
        fetchAvailableCoupons(),
      ]);
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load customer data');
    } finally {
      setLoading(false);
    }
  };

  const fetchUtilizedServices = async () => {
    if (!phone) return;

    try {
      const cleanPhone = phone.replace(/\D/g, '').slice(0, 10);
      const response = await billsApi.getAll({ search: cleanPhone, limit: 1000 });

      if (response.success && response.data) {
        const servicesMap = new Map();
        
        response.data.forEach((bill) => {
          if (bill.services && Array.isArray(bill.services)) {
            bill.services.forEach((service) => {
              const serviceName = service.name || service.service_name || 'Unknown Service';
              const serviceAmount = Number(service.service_charge || service.price || 0);
              
              if (servicesMap.has(serviceName)) {
                const existing = servicesMap.get(serviceName);
                existing.count += 1;
                existing.total += serviceAmount;
                existing.bills.push(bill.bill_number || bill.billNumber || 'N/A');
              } else {
                servicesMap.set(serviceName, {
                  name: serviceName,
                  count: 1,
                  total: serviceAmount,
                  bills: [bill.bill_number || bill.billNumber || 'N/A'],
                });
              }
            });
          }
        });

        const services = Array.from(servicesMap.values()).sort((a, b) => b.total - a.total);
        setUtilizedServices(services);
        
        const totalSpent = services.reduce((sum, s) => sum + s.total, 0);
        setStatistics((prev) => ({
          ...prev,
          totalServices: services.length,
          totalSpent,
        }));
      }
    } catch (err) {
      console.error('Error fetching utilized services:', err);
    }
  };

  const fetchPendingWorks = async () => {
    if (!phone) return;

    try {
      const cleanPhone = phone.replace(/\D/g, '').slice(0, 10);
      const response = await billsApi.getAll({ 
        search: cleanPhone, 
        limit: 1000,
        workStatus: 'pending,under_processing'
      });

      if (response.success && response.data) {
        const pending = response.data.filter(
          (bill) => 
            bill.work_status === 'pending' || 
            bill.work_status === 'under_processing'
        );
        setPendingWorks(pending);
        setStatistics((prev) => ({
          ...prev,
          pendingWorksCount: pending.length,
        }));
      }
    } catch (err) {
      console.error('Error fetching pending works:', err);
    }
  };

  const fetchAppliedJobs = async () => {
    if (!phone) return;

    try {
      const cleanPhone = phone.replace(/\D/g, '').slice(0, 10);
      const baseUrl = getApiBaseUrl();
      const token = localStorage.getItem('token');
      const authHeaders = token
        ? { Authorization: `Bearer ${token}` }
        : {};

      const jobsByKey = new Map();

      const upsertJob = (job) => {
        if (!job) return;
        const key =
          job.jobAlertId ||
          job.id ||
          `${(job.notificationName || job.title || '').toLowerCase()}|${job.billNumber || job.billId || ''}`;
        if (!key || key === '|') return;
        if (!jobsByKey.has(key)) {
          jobsByKey.set(key, job);
        }
      };

      // 1) Real applications only (not all job alerts)
      try {
        const appsResponse = await axios.get(
          `${baseUrl}/api/user-applications/user/${cleanPhone}`,
          { headers: authHeaders }
        );
        if (appsResponse.data?.success && Array.isArray(appsResponse.data.data)) {
          appsResponse.data.data.forEach((app) => {
            upsertJob({
              id: app.id,
              jobAlertId: app.jobAlertId,
              notificationName:
                app.notificationName ||
                app.jobAlert?.notificationName ||
                'Applied Job',
              organization:
                app.organization || app.jobAlert?.organization || '',
              qualification:
                app.qualification || app.jobAlert?.qualification || '',
              numberOfVacancies:
                app.numberOfVacancies ??
                app.jobAlert?.numberOfVacancies ??
                0,
              lastDate: app.lastDate || app.jobAlert?.lastDate || null,
              onlineApplicationLink:
                app.onlineApplicationLink ||
                app.jobAlert?.onlineApplicationLink ||
                null,
              isUrgent: app.isUrgent || app.jobAlert?.isUrgent || false,
              applicationStatus: app.applicationStatus || 'applied',
              billNumber: app.billNumber || null,
              billId: app.billId || null,
              appliedAt: app.createdAt || null,
              source: 'application',
            });
          });
        }
      } catch (appsErr) {
        console.warn('Could not load user applications:', appsErr?.message || appsErr);
      }

      // 2) Fallback: Online Application lines on this customer's bills
      // (covers historical bills created before user_applications sync)
      try {
        const billsResponse = await billsApi.getAll({
          search: cleanPhone,
          limit: 1000,
        });
        if (billsResponse.success && Array.isArray(billsResponse.data)) {
          billsResponse.data.forEach((bill) => {
            const billPhone = String(bill.customer_phone || bill.customerPhone || '')
              .replace(/\D/g, '')
              .slice(-10);
            if (billPhone && billPhone !== cleanPhone) return;

            const services = Array.isArray(bill.services) ? bill.services : [];
            services.forEach((service) => {
              const serviceName = String(
                service.name || service.service_name || service.display_name || ''
              ).toLowerCase();
              const isOnlineApp =
                serviceName === 'online application' ||
                serviceName.includes('online application');
              if (!isOnlineApp) return;

              const sub =
                service.sub_service ||
                service.subService ||
                (String(service.display_name || service.displayName || '')
                  .replace(/^online application\s*[-–—:]?\s*/i, '')
                  .trim());
              const jobTitle = String(sub || '').trim();
              if (!jobTitle || /^online application$/i.test(jobTitle)) return;

              upsertJob({
                id: `bill-${bill.id || bill.bill_number}-${jobTitle}`,
                jobAlertId: null,
                notificationName: jobTitle,
                organization: '',
                qualification: '',
                numberOfVacancies: 0,
                lastDate: null,
                onlineApplicationLink: null,
                isUrgent: false,
                applicationStatus: 'applied',
                billNumber: bill.bill_number || bill.billNumber || null,
                billId: bill.id || null,
                appliedAt: bill.bill_date || bill.created_at || null,
                source: 'bill',
              });
            });
          });
        }
      } catch (billsErr) {
        console.warn('Could not derive applied jobs from bills:', billsErr?.message || billsErr);
      }

      const jobs = Array.from(jobsByKey.values()).sort((a, b) => {
        const da = a.appliedAt ? new Date(a.appliedAt).getTime() : 0;
        const db = b.appliedAt ? new Date(b.appliedAt).getTime() : 0;
        return db - da;
      });

      setAppliedJobs(jobs);
      setStatistics((prev) => ({
        ...prev,
        appliedJobsCount: jobs.length,
      }));
    } catch (err) {
      console.error('Error fetching applied jobs:', err);
      setAppliedJobs([]);
      setStatistics((prev) => ({
        ...prev,
        appliedJobsCount: 0,
      }));
    }
  };

  const fetchCustomerCoupons = async () => {
    if (!phone) return;

    try {
      const cleanPhone = phone.replace(/\D/g, '').slice(0, 10);
      // Send biller token so API returns only this mitra/franchise group's coupons.
      const response = await customerCouponsApi.getByPhone(cleanPhone);

      if (response.success) {
        const coupons = response.coupons || [];
        setCustomerCoupons(coupons);
        const activeCount = coupons.filter((c) => c.status === 'active' || c.status === 'used').length;
        setStatistics((prev) => ({
          ...prev,
          activeCoupons: activeCount,
        }));
      }
    } catch (err) {
      console.error('Error fetching customer coupons:', err);
      const msg = String(err?.message || err?.response?.data?.message || '');
      if (
        err.response?.status === 401 ||
        msg.toLowerCase().includes('token') ||
        msg.toLowerCase().includes('unauthorized') ||
        msg.toLowerCase().includes('expired')
      ) {
        toast.error('Session expired. Please log in again and refresh.');
      } else if (msg) {
        toast.error(msg);
      } else {
        toast.error('Failed to load coupons. Please try again.');
      }
    }
  };

  const fetchAvailableCoupons = async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const token = localStorage.getItem('token');
      
      // Only fetch if token exists (this endpoint requires authentication)
      if (!token) {
        console.log('No token available, skipping available coupons fetch');
        setAvailableCoupons([]);
        return;
      }

      const response = await axios.get(`${baseUrl}/api/coupons`, {
        params: { status: 'active', limit: 1000 },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setAvailableCoupons(response.data.coupons || []);
      }
    } catch (err) {
      console.error('Failed to load available coupons:', err);
      // Don't show error toast for missing token - this is expected for public access
      if (err.response?.status !== 401) {
        toast.error('Failed to load available coupons');
      }
    }
  };

  const handleQuickClaim = async (coupon) => {
    if (coupon.status !== 'active') {
      toast.error('This coupon is not active');
      return;
    }

    // Get service details from coupon
    let serviceDetails = null;
    if (coupon.service_id || coupon.service_name) {
      try {
        const servicesResponse = await servicesApi.getAll('', '', false);
        if (servicesResponse.success && servicesResponse.data) {
          serviceDetails = servicesResponse.data.find(
            (s) => s.id === coupon.service_id || s.name === coupon.service_name
          );
        }
      } catch (err) {
        console.error('Error fetching service details:', err);
      }
    }

    // Prepare service entry - use service details if found, otherwise create from coupon
    const serviceEntry = serviceDetails
      ? {
          id: serviceDetails.id,
          name: serviceDetails.name,
          displayName: serviceDetails.name,
          mainCategory: serviceDetails.main_category || '',
          subCategory: serviceDetails.sub_category || '',
          price: Number(coupon.actual_price || serviceDetails.price || 0),
          serviceCharge: Number(coupon.offer_price || coupon.final_price || (coupon.actual_price || serviceDetails.price || 0)),
          challanaAmount: Number(serviceDetails.challana || 0),
          quantity: 1,
          total: Number(coupon.offer_price || coupon.final_price || (coupon.actual_price || serviceDetails.price || 0)) + Number(serviceDetails.challana || 0),
        }
      : coupon.service_name
      ? {
          id: `coupon-service-${coupon.id}`,
          name: coupon.service_name,
          displayName: coupon.service_name,
          mainCategory: '',
          subCategory: '',
          price: Number(coupon.actual_price || 0),
          serviceCharge: Number(coupon.offer_price || coupon.final_price || coupon.actual_price || 0),
          challanaAmount: 0,
          quantity: 1,
          total: Number(coupon.offer_price || coupon.final_price || coupon.actual_price || 0),
        }
      : null;

    // Prepare bill data with coupon details
    const billData = {
      customerData: {
        name: customerName || coupon.customer_name || '',
        phone: phone.replace(/\D/g, '').slice(0, 10),
        profession: '',
        address: '',
        city: '',
        pincode: '',
        remarks: `Coupon: ${coupon.name || coupon.coupon_name || 'N/A'}`,
        workStatus: 'completed',
      },
      selectedServices: serviceEntry ? [serviceEntry] : [],
      couponData: {
        couponId: coupon.id, // Store coupon ID to mark as used
        couponName: coupon.name || coupon.coupon_name,
        originalPrice: coupon.actual_price || 0,
        offerPrice: coupon.offer_price || coupon.final_price || 0,
        discountAmount: (coupon.actual_price || 0) - (coupon.offer_price || coupon.final_price || 0),
      },
    };

    // Store in sessionStorage for SmartBillWithQR to pick up
    sessionStorage.setItem('couponBillData', JSON.stringify(billData));
    
    toast.success('Opening bill form with coupon details...');
    navigate('/bills/create');
  };

  const handleUpdateCoupons = async () => {
    if (!phone) {
      toast.error('Phone number is required');
      return;
    }

    try {
      setUpdatingCoupons(true);
      const cleanPhone = phone.replace(/\D/g, '').slice(0, 10);
      const baseUrl = getApiBaseUrl();

      // Get all coupons for this customer (not just active - backend checks all for duplicates)
      // Current coupons for this customer (scoped to logged-in mitra/biller).
      const currentCouponsResponse = await customerCouponsApi.getByPhone(cleanPhone);

      const currentCoupons = currentCouponsResponse.success
        ? (currentCouponsResponse.coupons || [])
        : [];

      // Get all available coupons from management page
      // This endpoint requires authentication, so check token first
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required to update coupons. Please log in.');
        return;
      }

      const availableResponse = await axios.get(`${baseUrl}/api/coupons`, {
        params: { status: 'active', limit: 1000 },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!availableResponse.data.success) {
        throw new Error('Failed to fetch available coupons');
      }

      const availableCoupons = availableResponse.data.coupons || [];
      let assignedCount = 0;
      let skippedCount = 0;

      // Assign coupons that are not already assigned and are active and not expired
      for (const coupon of availableCoupons) {
        // Check if coupon is expired
        const validUntil = coupon.valid_until || coupon.validUntil;
        const isExpired = validUntil ? new Date(validUntil) < new Date() : false;
        
        if (isExpired) {
          console.log(`⏭️ Skipping expired coupon: ${coupon.name || coupon.coupon_name} (Valid until: ${validUntil})`);
          skippedCount++;
          continue;
        }
        // Match by template id only — not by name (other mitras may share titles).
        const templateId = String(coupon.id || coupon.coupon_id || '');
        if (!templateId) {
          skippedCount++;
          continue;
        }
        const alreadyAssigned = currentCoupons.some((c) => {
          const customerTemplateId = String(c.parent_coupon_id || '');
          const customerId = String(c.id || c.coupon_id || '');
          return customerTemplateId === templateId || customerId === templateId;
        });

        // Check if coupon is used
        const isUsed = coupon.status === 'used' || coupon.used_at;

        if (!alreadyAssigned && !isUsed && coupon.status === 'active') {
          try {
            await axios.post(
              `${baseUrl}/api/coupons/assign`,
              {
                coupon_id: coupon.id || coupon.coupon_id,
                customer_phone: cleanPhone,
                customer_name: customerName,
              },
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
              }
            );
            assignedCount++;
          } catch (assignErr) {
            console.error(`Failed to assign coupon ${coupon.id}:`, assignErr);
            skippedCount++;
          }
        } else {
          skippedCount++;
        }
      }

      // Refresh customer coupons
      await fetchCustomerCoupons();

      if (assignedCount > 0) {
        toast.success(`Successfully assigned ${assignedCount} coupon(s)!`);
      } else {
        toast.info('No new coupons to assign. All active coupons are already assigned.');
      }
    } catch (err) {
      console.error('Failed to update coupons:', err);
      toast.error(err.response?.data?.message || err.message || 'Failed to update coupons');
    } finally {
      setUpdatingCoupons(false);
    }
  };

  const getCouponAmount = (coupon) => {
    if (coupon.offer_price) return Number(coupon.offer_price);
    if (coupon.final_price) return Number(coupon.final_price);
    if (coupon.actual_price) return Number(coupon.actual_price);
    return 0;
  };

  // Include active, used, and expired coupons for display
  const carouselRef = useRef(null);

  // Filter coupons based on status filter (active, used, expired)
  const filteredCoupons = useMemo(() => {
    return customerCoupons.filter((c) => {
      if (!c) return false;
      const status = c.status || 'active';
      const validUntil = c.valid_until || c.validUntil;
      const isExpired = validUntil ? new Date(validUntil) < new Date() : false;
      const isUsed = status === 'used';
      
      if (couponStatusFilter === 'active') {
        // Active: status is 'active' and not expired, not used
        return status === 'active' && !isExpired && !isUsed;
      } else if (couponStatusFilter === 'used') {
        // Used: status is 'used'
        return isUsed;
      } else if (couponStatusFilter === 'expired') {
        // Expired: status is 'expired' or valid_until is past
        return status === 'expired' || isExpired;
      }
      return true;
    });
  }, [customerCoupons, couponStatusFilter]);

  // Use filteredCoupons instead of activeCoupons
  const activeCoupons = filteredCoupons;

  // Intersection Observer for scroll animations with enhanced effects.
  // Must re-run when `loading` becomes false — cards mount only then, and
  // without `in-view` they stay at opacity: 0 (blank Active tab on first land).
  useEffect(() => {
    if (loading || activeCoupons.length === 0) return;

    let observer;
    let cards = [];
    const frame = requestAnimationFrame(() => {
      if (!carouselRef.current) return;

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('in-view');
              const card = entry.target;
              const starsContainer = card.querySelector('.floating-stars');
              const flowersContainer = card.querySelector('.floating-flowers');
              if (starsContainer) starsContainer.classList.add('animate');
              if (flowersContainer) flowersContainer.classList.add('animate');
            }
          });
        },
        {
          threshold: 0.2,
          rootMargin: '0px',
        }
      );

      cards = Array.from(carouselRef.current.querySelectorAll('.carousel-item'));
      cards.forEach((card) => {
        observer.observe(card);
        // Fallback: elements already in the viewport sometimes miss the first
        // intersection callback depending on layout timing.
        const rect = card.getBoundingClientRect();
        const inViewport =
          rect.top < window.innerHeight &&
          rect.bottom > 0 &&
          rect.width > 0 &&
          rect.height > 0;
        if (inViewport) {
          card.classList.add('in-view');
          const starsContainer = card.querySelector('.floating-stars');
          const flowersContainer = card.querySelector('.floating-flowers');
          if (starsContainer) starsContainer.classList.add('animate');
          if (flowersContainer) flowersContainer.classList.add('animate');
        }
      });
    });

    return () => {
      cancelAnimationFrame(frame);
      if (observer) {
        cards.forEach((card) => observer.unobserve(card));
        observer.disconnect();
      }
    };
  }, [activeCoupons, loading]);

  return (
    <div className="customer-dashboard-page">
      <header className="customer-dashboard-header">
        <div className="header-content">
          <div>
            <h1>Customer Dashboard</h1>
            <p>
              <strong>{customerName || 'Customer'}</strong> ({phone})
              {billNumber && <span> • Bill: {billNumber}</span>}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              className="btn btn-outline"
              onClick={() => navigate('/shop-orders')}
              title="Citizen redeem orders for your shop"
            >
              <i className="fas fa-receipt"></i> Shop Orders
            </button>
            <button
              className="btn btn-outline"
              onClick={() => {
                const digits = String(phone || '').replace(/\D/g, '').slice(-10);
                if (digits.length !== 10) {
                  toast.error('Valid customer phone required to share coupons');
                  return;
                }
                const password = digits.slice(-4);
                const base =
                  process.env.REACT_APP_PRODUCTION_URL ||
                  (typeof window !== 'undefined' ? window.location.origin : 'https://services.knls.in');
                const loginUrl = `${String(base).replace(/\/$/, '')}/user-login?phone=${digits}&password=${password}&redirect=${encodeURIComponent('/user/coupons')}`;
                const name = customerName || 'Customer';
                const message =
                  `Hi ${name}!\n` +
                  `Your LOCSY / KNLS coupons are ready.\n\n` +
                  `🔓 Auto-login coupons link:\n${loginUrl}\n\n` +
                  `One-click login → see your coupons → Redeem.\n` +
                  `Password tip: last 4 digits of your mobile number.`;
                window.open(
                  `https://wa.me/91${digits}?text=${encodeURIComponent(message)}`,
                  '_blank',
                  'noopener,noreferrer',
                );
              }}
              title="WhatsApp auto-login link to customer coupons"
            >
              <i className="fas fa-ticket-alt"></i> Share Coupons
            </button>
            <button className="btn btn-outline" onClick={() => navigate('/bills/create')}>
              <i className="fas fa-arrow-left"></i> Back to Bills
            </button>
          </div>
        </div>
      </header>

      {/* Hide tabs in history mode - only show coupons tab */}
      {!isHistoryMode && (
        <div className="dashboard-tabs">
          <button
            className={`tab-btn ${activeTab === 'services' ? 'active' : ''}`}
            onClick={() => setActiveTab('services')}
          >
            <i className="fas fa-list"></i> Services ({statistics.totalServices})
          </button>
          <button
            className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            <i className="fas fa-clock"></i> Pending Works ({statistics.pendingWorksCount})
          </button>
          <button
            className={`tab-btn ${activeTab === 'jobs' ? 'active' : ''}`}
            onClick={() => setActiveTab('jobs')}
          >
            <i className="fas fa-briefcase"></i> Applied Jobs ({statistics.appliedJobsCount})
          </button>
          <button
            className={`tab-btn ${activeTab === 'coupons' ? 'active' : ''}`}
            onClick={() => setActiveTab('coupons')}
          >
            <i className="fas fa-ticket-alt"></i> Coupons ({statistics.activeCoupons})
          </button>
          <button
            className="tab-btn"
            onClick={() =>
              navigate(`/user/coupons?phone=${encodeURIComponent(phone || '')}`)
            }
            title="Open customer wallet (Redeem → order)"
          >
            <i className="fas fa-external-link-alt"></i> Customer wallet
          </button>
        </div>
      )}
      
      {/* Show only coupons tab header in history mode */}
      {isHistoryMode && (
        <div className="dashboard-tabs" style={{ justifyContent: 'center' }}>
          <button
            className="tab-btn active"
            style={{ cursor: 'default' }}
          >
            <i className="fas fa-ticket-alt"></i> Your Coupons ({statistics.activeCoupons})
          </button>
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading...</p>
        </div>
      ) : (
        <div className="dashboard-content">
          {/* In history mode, only show coupons tab content */}
          {isHistoryMode && activeTab !== 'coupons' && (
            <div className="tab-content">
              <div className="empty-state">
                <i className="fas fa-lock"></i>
                <p>Access restricted. Only coupons are available in this view.</p>
              </div>
            </div>
          )}
          
          {!isHistoryMode && activeTab === 'services' && (
            <div className="tab-content">
              <div className="section-header">
                <h2>Previously Utilized Services</h2>
                <div className="total-summary">
                  Total Spent: <strong>{formatCurrency(statistics.totalSpent)}</strong>
                </div>
              </div>
              {utilizedServices.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox"></i>
                  <p>No services utilized yet</p>
                </div>
              ) : (
                <div className="services-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Service Name</th>
                        <th>Count</th>
                        <th>Total Amount</th>
                        <th>Bill Numbers</th>
                      </tr>
                    </thead>
                    <tbody>
                      {utilizedServices.map((service, idx) => (
                        <tr key={idx}>
                          <td>{service.name}</td>
                          <td>{service.count}</td>
                          <td>{formatCurrency(service.total)}</td>
                          <td>
                            <div className="bill-numbers">
                              {service.bills.slice(0, 3).map((bill, i) => (
                                <span key={i} className="bill-badge">{bill}</span>
                              ))}
                              {service.bills.length > 3 && (
                                <span className="bill-badge more">+{service.bills.length - 3} more</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {!isHistoryMode && activeTab === 'pending' && (
            <div className="tab-content">
              <div className="section-header">
                <h2>Pending Works</h2>
              </div>
              {pendingWorks.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-check-circle"></i>
                  <p>No pending works</p>
                </div>
              ) : (
                <div className="pending-works-list">
                  {pendingWorks.map((work) => (
                    <div key={work.id} className="work-card">
                      <div className="work-header">
                        <span className="bill-number">{work.bill_number || work.billNumber || 'N/A'}</span>
                        <span className={`status-badge ${work.work_status}`}>
                          {work.work_status === 'pending' ? 'Pending' : 'Under Processing'}
                        </span>
                      </div>
                      <div className="work-date">
                        Date: {new Date(work.bill_date || work.billDate || work.created_at).toLocaleDateString('en-IN')}
                      </div>
                      {work.work_remark && (
                        <div className="work-remark">
                          <strong>Remarks:</strong> {work.work_remark}
                        </div>
                      )}
                      {work.services && work.services.length > 0 && (
                        <div className="work-services">
                          <strong>Services:</strong>{' '}
                          {work.services.map((s) => s.name || s.service_name).join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {!isHistoryMode && activeTab === 'jobs' && (
            <div className="tab-content">
              <div className="section-header">
                <h2>Applied Jobs</h2>
                <button
                  className="btn btn-primary"
                  onClick={fetchAppliedJobs}
                >
                  <i className="fas fa-sync"></i> Refresh
                </button>
              </div>
              {appliedJobs.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-briefcase"></i>
                  <p>No applied jobs yet</p>
                  <p style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: 8 }}>
                    Jobs appear here after an Online Application bill is created for this customer.
                  </p>
                  <button
                    className="btn btn-primary"
                    onClick={fetchAppliedJobs}
                  >
                    <i className="fas fa-sync"></i> Refresh
                  </button>
                </div>
              ) : (
                <div className="job-alerts-list">
                  {appliedJobs.map((job) => (
                    <div key={job.id} className="job-card">
                      <div className="job-header">
                        <h3>{job.notificationName || 'Applied Job'}</h3>
                        <span className="status-badge" style={{ background: '#10b981', color: '#fff' }}>
                          {(job.applicationStatus || 'applied').replace(/_/g, ' ')}
                        </span>
                      </div>
                      {job.organization && (
                        <div className="job-org">{job.organization}</div>
                      )}
                      <div className="job-details">
                        {job.numberOfVacancies > 0 && (
                          <div className="job-detail">
                            <i className="fas fa-users"></i> {job.numberOfVacancies} Vacancies
                          </div>
                        )}
                        {job.qualification && (
                          <div className="job-detail">
                            <i className="fas fa-graduation-cap"></i> {job.qualification}
                          </div>
                        )}
                        {job.lastDate && (
                          <div className="job-detail">
                            <i className="fas fa-calendar"></i> Last Date:{' '}
                            {new Date(job.lastDate).toLocaleDateString('en-IN')}
                          </div>
                        )}
                        {job.billNumber && (
                          <div className="job-detail">
                            <i className="fas fa-receipt"></i> Bill: {job.billNumber}
                          </div>
                        )}
                        {job.appliedAt && (
                          <div className="job-detail">
                            <i className="fas fa-check-circle"></i> Applied:{' '}
                            {new Date(job.appliedAt).toLocaleDateString('en-IN')}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'coupons' && (
            <div className="tab-content">
              <div className="section-header">
                <h2>Coupons</h2>
                <button
                  className="btn btn-primary"
                  onClick={handleUpdateCoupons}
                  disabled={updatingCoupons}
                >
                  <i className="fas fa-sync"></i>{' '}
                  {updatingCoupons ? 'Updating...' : 'Update Coupons'}
                </button>
              </div>

              {/* Status Filter Tabs */}
              <Box sx={{ mb: 3, mt: 2 }}>
                <Tabs
                  value={couponStatusFilter}
                  onChange={(e, newValue) => {
                    setCouponStatusFilter(newValue);
                  }}
                  variant={isMobile ? 'scrollable' : 'standard'}
                  scrollButtons="auto"
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    '& .MuiTab-root': {
                      minWidth: { xs: 80, sm: 120 },
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      fontWeight: 600,
                      textTransform: 'none',
                    },
                  }}
                >
                  <Tab 
                    label={`Active (${customerCoupons.filter(c => {
                      if (!c) return false;
                      const status = c.status || 'active';
                      const validUntil = c.valid_until || c.validUntil;
                      const isExpired = validUntil ? new Date(validUntil) < new Date() : false;
                      const isUsed = status === 'used';
                      return status === 'active' && !isExpired && !isUsed;
                    }).length})`}
                    value="active"
                  />
                  <Tab 
                    label={`Used (${customerCoupons.filter(c => {
                      if (!c) return false;
                      return c.status === 'used';
                    }).length})`}
                    value="used"
                  />
                  <Tab 
                    label={`Expired (${customerCoupons.filter(c => {
                      if (!c) return false;
                      const status = c.status || 'active';
                      const validUntil = c.valid_until || c.validUntil;
                      return status === 'expired' || (validUntil ? new Date(validUntil) < new Date() : false);
                    }).length})`}
                    value="expired"
                  />
                </Tabs>
              </Box>

              {loading ? (
                <div className="empty-state">
                  <i className="fas fa-spinner fa-spin"></i>
                  <p>Loading coupons...</p>
                </div>
              ) : activeCoupons.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-ticket-alt"></i>
                  <p>
                    {couponStatusFilter === 'active' && 'No active coupons'}
                    {couponStatusFilter === 'used' && 'No used coupons'}
                    {couponStatusFilter === 'expired' && 'No expired coupons'}
                  </p>
                  {couponStatusFilter === 'active' && (
                    <button
                      className="btn btn-primary"
                      onClick={handleUpdateCoupons}
                      disabled={updatingCoupons}
                    >
                      <i className="fas fa-sync"></i> Update Coupons
                    </button>
                  )}
                </div>
              ) : (
                <div className="coupons-carousel-wrapper">
                  <div className="coupons-carousel" ref={carouselRef}>
                    {activeCoupons.map((coupon, index) => {
                    const originalAmount = Number(coupon.actual_price || coupon.original_price || 0);
                    const offerAmount = getCouponAmount(coupon);
                    const discountAmount = Math.max(originalAmount - offerAmount, 0);
                    const isUsed = coupon.status === 'used';
                    // Check if coupon is expired
                    const validUntil = coupon.valid_until || coupon.validUntil;
                    const isExpired = validUntil ? new Date(validUntil) < new Date() : false;

                    // Generate unique color for each coupon - expanded color palette
                    const colorSchemes = [
                      { primary: '#6366f1', secondary: '#8b5cf6', accent: '#a5b4fc', name: 'Indigo' },
                      { primary: '#ec4899', secondary: '#f472b6', accent: '#fbcfe8', name: 'Pink' },
                      { primary: '#10b981', secondary: '#34d399', accent: '#6ee7b7', name: 'Green' },
                      { primary: '#f59e0b', secondary: '#fbbf24', accent: '#fde68a', name: 'Amber' },
                      { primary: '#3b82f6', secondary: '#60a5fa', accent: '#93c5fd', name: 'Blue' },
                      { primary: '#8b5cf6', secondary: '#a78bfa', accent: '#c4b5fd', name: 'Purple' },
                      { primary: '#ef4444', secondary: '#f87171', accent: '#fca5a5', name: 'Red' },
                      { primary: '#14b8a6', secondary: '#5eead4', accent: '#99f6e4', name: 'Teal' },
                      { primary: '#f97316', secondary: '#fb923c', accent: '#fdba74', name: 'Orange' },
                      { primary: '#06b6d4', secondary: '#22d3ee', accent: '#67e8f9', name: 'Cyan' },
                      { primary: '#a855f7', secondary: '#c084fc', accent: '#d8b4fe', name: 'Violet' },
                      { primary: '#84cc16', secondary: '#a3e635', accent: '#bef264', name: 'Lime' },
                      { primary: '#eab308', secondary: '#facc15', accent: '#fde047', name: 'Yellow' },
                      { primary: '#06b6d4', secondary: '#0891b2', accent: '#22d3ee', name: 'Sky' },
                      { primary: '#d946ef', secondary: '#e879f9', accent: '#f0abfc', name: 'Fuchsia' },
                      { primary: '#22c55e', secondary: '#4ade80', accent: '#86efac', name: 'Emerald' },
                    ];
                    // Use coupon ID or name hash to determine color
                    const couponId = coupon.id || coupon.name || index;
                    const hashValue = typeof couponId === 'string' 
                      ? couponId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
                      : couponId;
                    const colorIndex = Math.abs(hashValue) % colorSchemes.length;
                    const colorScheme = colorSchemes[colorIndex] || colorSchemes[0];

                    // Touch event handlers for mobile - differentiate tap vs scroll
                    const handleTouchStart = (e) => {
                      const touch = e.touches[0];
                      touchStartRef.current = {
                        x: touch.clientX,
                        y: touch.clientY,
                        time: Date.now(),
                        index: index, // Track which card was touched
                      };
                      isScrollingRef.current = false;
                    };

                    const handleTouchMove = (e) => {
                      // Only check if this is the card we're tracking
                      if (touchStartRef.current.index !== index) return;
                      if (!touchStartRef.current.x && !touchStartRef.current.y) return;
                      
                      const touch = e.touches[0];
                      const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
                      const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
                      
                      // If moved more than 10px horizontally, consider it scrolling
                      // Vertical movement less than horizontal means scrolling
                      if (deltaX > 10 || (deltaX > 5 && deltaX > deltaY)) {
                        isScrollingRef.current = true;
                      }
                    };

                    const handleTouchEnd = (e) => {
                      // Only process if this is the card we're tracking
                      if (touchStartRef.current.index !== index) return;
                      if (!touchStartRef.current.x && !touchStartRef.current.y) return;
                      
                      const touch = e.changedTouches[0];
                      const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
                      const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
                      const deltaTime = Date.now() - touchStartRef.current.time;
                      
                      const wasScrolling = isScrollingRef.current;
                      
                      // Reset touch start
                      touchStartRef.current = { x: 0, y: 0, time: 0, index: -1 };
                      isScrollingRef.current = false;
                      
                      // Only reveal if it was a tap (not scroll) and not used/expired
                      // Tap: moved less than 10px, took less than 300ms, and wasn't scrolling
                      if (!wasScrolling && deltaX < 10 && deltaY < 10 && deltaTime < 300) {
                        if (!isUsed && !isExpired) {
                          e.preventDefault();
                          e.stopPropagation();
                          setOpenScratchCard(index);
                        }
                      }
                    };

                    // Click handler for desktop
                    const handleClick = (e) => {
                      // On mobile, prevent click if touch was used (double-tap prevention)
                      if (touchStartRef.current.index === index) {
                        return;
                      }
                      if (!isUsed && !isExpired) {
                        setOpenScratchCard(index);
                      }
                    };

                    return (
                      <React.Fragment key={`admin-coupon-${coupon.id}-${coupon.status || 'active'}`}>
                        <div 
                          className={`coupon-card carousel-item ${isUsed ? 'used-coupon' : ''} ${isExpired ? 'expired-coupon' : ''} ${openScratchCard === index ? 'active' : ''}`}
                          style={{
                            animationDelay: `${index * 0.1}s`,
                            ...(isUsed 
                              ? { opacity: 0.8, backgroundColor: '#f5f5f5', border: '2px solid #9ca3af' }
                              : isExpired
                              ? { opacity: 0.8, backgroundColor: '#fef2f2', border: '2px solid #fca5a5' }
                              : { 
                                  border: '1px solid rgba(255, 107, 0, 0.35)',
                                  boxShadow: '0 8px 20px rgba(255, 107, 0, 0.12)'
                                })
                          }}
                          onTouchStart={handleTouchStart}
                          onTouchMove={handleTouchMove}
                          onTouchEnd={handleTouchEnd}
                          onClick={handleClick}
                        >
                          {/* Floating decorative text */}
                          {!isUsed && !isExpired && (
                            <>
                              <div className="floating-text floating-text-1">COUPONS</div>
                              <div className="floating-text floating-text-2">KNLS</div>
                              <div className="floating-text floating-text-3">OFFERS</div>
                              <div className="floating-stars">
                                {[...Array(8)].map((_, i) => (
                                  <span key={i} className="star-emoji" style={{
                                    left: `${Math.random() * 100}%`,
                                    top: `${Math.random() * 100}%`,
                                    animationDelay: `${Math.random() * 2}s`
                                  }}>⭐</span>
                                ))}
                              </div>
                              <div className="floating-flowers">
                                {[...Array(6)].map((_, i) => (
                                  <span key={i} className="flower-emoji" style={{
                                    left: `${Math.random() * 100}%`,
                                    top: `${Math.random() * 100}%`,
                                    animationDelay: `${Math.random() * 2}s`
                                  }}>🌸</span>
                                ))}
                              </div>
                            </>
                          )}
                          <div 
                            className="coupon-header"
                            style={{ 
                              background: isUsed 
                                ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                                : isExpired
                                ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                                : 'linear-gradient(135deg, #ff6b00 0%, #ff8c38 55%, #ffb347 100%)'
                            }}
                          >
                            <h3 style={isUsed || isExpired ? { color: '#fff' } : {}}>{coupon.name || coupon.coupon_name || 'Coupon'}</h3>
                            {(coupon.mitra_name || coupon.shop_name) && (
                              <div style={{ fontSize: '12px', fontWeight: 700, color: '#fff', marginTop: 2 }}>
                                🏪 {coupon.mitra_name || coupon.shop_name}
                              </div>
                            )}
                            <span className={`coupon-status ${isUsed ? 'used' : isExpired ? 'expired' : 'active'}`}>
                              {isUsed ? 'Used' : isExpired ? 'Expired' : 'Active'}
                            </span>
                          </div>
                          <div 
                            className="coupon-preview"
                          >
                            {isUsed ? (
                              <div className="coupon-body-preview">
                                <div className="coupon-service">
                                  <i className="fas fa-tag"></i> {coupon.service_name || 'Service'}
                                </div>
                                <div className="coupon-code-preview">
                                  <div className="code-display">{coupon.coupon_code || coupon.code || 'N/A'}</div>
                                  <div className="code-label">Already Used</div>
                                </div>
                              </div>
                            ) : isExpired ? (
                              <div className="coupon-body-preview">
                                <div className="coupon-service">
                                  <i className="fas fa-tag"></i> {coupon.service_name || 'Service'}
                                </div>
                                <div className="coupon-code-preview">
                                  <div className="code-display expired-code">{coupon.coupon_code || coupon.code || 'N/A'}</div>
                                  <div className="code-label expired-label">Expired</div>
                                </div>
                              </div>
                            ) : (
                              <div 
                                className="scratch-preview"
                              >
                                <div className="scratch-preview-content">
                                  <i className="fas fa-hand-pointer"></i>
                                  <h4>SCRATCH TO UNLOCK</h4>
                                  <p>Tap to reveal like Google Pay</p>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="coupon-actions">
                            <button
                              className="btn btn-success btn-claim"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!isUsed && !isExpired) {
                                  handleQuickClaim(coupon);
                                }
                              }}
                              disabled={isUsed || isExpired}
                              style={
                                isUsed || isExpired 
                                  ? { opacity: 0.5, cursor: 'not-allowed', backgroundColor: '#ccc' } 
                                  : {}
                              }
                            >
                              <i className="fas fa-check-circle"></i> {isUsed ? 'Already Used' : isExpired ? 'Expired' : 'Quick Claim'}
                            </button>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                  </div>
                </div>
              )}

              {/* Coupon Reels Modal - Instagram Reels style */}
              <CouponReelsModal
                coupons={activeCoupons}
                initialIndex={typeof openScratchCard === 'number' ? openScratchCard : 0}
                isOpen={typeof openScratchCard === 'number'}
                onClose={() => setOpenScratchCard(null)}
                onClaim={() => {
                  setOpenScratchCard(null);
                }}
                onUtilizeLater={() => {
                  toast.info('Coupon saved for later use', { autoClose: 2000 });
                }}
                formatCurrency={formatCurrency}
                getCouponAmount={getCouponAmount}
                phone={phone}
                customerName={customerName}
                navigate={navigate}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerDashboard;
